import type { MissingEvidenceKey, Recommendation } from '../../api/types'
import { MISSING_EVIDENCE_LABEL, RECOMMENDATION_LABEL } from '../pipeline/labels'
import { decisionAddsInformation } from './verdictRules'
import styles from './DealPanel.module.css'

/**
 * What DealPulse concludes, and where the user disagrees.
 *
 * The conclusion of the whole panel, so it is the only block-level element on it.
 * Everything above is chips and prose at 12–14px; the verdict is set at 24px
 * inside a bounded region. That makes the reading order a size gradient rather
 * than a colour competition — signals, then verdict, then override — and it is
 * why a verdict rendered as another coloured pill read as one more tag.
 *
 * Placed after the evidence on purpose. Stated first, with signals underneath, it
 * reads as an assertion hunting for support; in this order it is a conclusion,
 * and the reason below becomes the bridge rather than a footnote.
 *
 * **Keep carries no reason** — `decision.py` returns None for it, because a deal
 * worth continuing needs no defence: the confirmed signals above it are the
 * defence. The block is laid out so that absence reads as complete rather than as
 * a line that failed to load; the verdict alone fills it.
 *
 * **Missing evidence sits beside the verdict, never inside it.** They answer
 * different questions: the verdict says what to do with the deal, the list says
 * what to establish on the next call. Keeping the second out of the first is the
 * change docs/19 made — the list used to be prose inside the recommendation, which
 * is how a deal with nothing wrong with it read as "Pause: missing a decision
 * maker". A Keep with three things still to confirm is the normal state of a live
 * deal after a first call, and it should read that way.
 *
 * **The user's decision appears only when it differs.** Repeating "you also said
 * Keep investing" under a Keep recommendation is a line that never changes
 * anyone's mind, and it dilutes the one case that matters. Agreement is already
 * visible in the decision control at the top of the panel.
 */
export function Verdict({
  recommendation,
  reason,
  citation,
  mandatoryReview,
  missingEvidence,
  decision,
}: {
  recommendation: Recommendation | null
  reason: string | null
  citation: string | null
  mandatoryReview: boolean
  missingEvidence: MissingEvidenceKey[]
  decision: Recommendation | null
}) {
  const overridden = decisionAddsInformation(decision, recommendation)

  return (
    <section className={styles.verdict} aria-label="Recommendation">
      <h3 className={styles.section}>Recommendation</h3>

      {recommendation ? (
        <p className={`${styles.verdictValue} ${styles[`verdict_${recommendation}`]}`}>
          {RECOMMENDATION_LABEL[recommendation]}
        </p>
      ) : (
        <p className={styles.verdictAbsent}>Not yet assessed</p>
      )}

      {reason ? <p className={styles.verdictReason}>{reason}</p> : null}
      {citation ? <blockquote className={styles.citation}>“{citation}”</blockquote> : null}
      {mandatoryReview ? (
        <p className={styles.flag}>⚑ Flagged for your review — no material change for several calls</p>
      ) : null}

      {missingEvidence.length > 0 ? (
        <div className={styles.missingEvidence}>
          <p className={styles.missingEvidenceTitle}>Still need to confirm</p>
          <ul className={styles.missingEvidenceList}>
            {missingEvidence.map((key) => (
              <li key={key}>{MISSING_EVIDENCE_LABEL[key]}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {overridden ? (
        <div className={styles.override}>
          {/* Labelled, because unlabelled it reads as a second thing DealPulse
              decided rather than the one thing on this panel the user decided. */}
          <p className={styles.overrideLabel}>Manual decision</p>
          <p className={styles.overrideHead}>
            You decided <strong>{RECOMMENDATION_LABEL[decision]}</strong>
          </p>
          <p className={styles.overrideBody}>
            {recommendation
              ? `Your decision stands. YourDealPulse still recommends ${RECOMMENDATION_LABEL[recommendation]} on the signals above.`
              : 'Your decision stands. YourDealPulse has not assessed this deal yet.'}
          </p>
        </div>
      ) : null}
    </section>
  )
}
